import type { Route } from "./+types/recipe";
import { brandName } from "@mealmanager/legal-content";
import Page from "~/recipes/recipe/page";
import gqlClientConnect from "src/api-config/graphql.config";
import { data } from "react-router";
import { CombinedGraphQLErrors } from "@apollo/client";
import {
  GetRecipeDocument,
  TagType,
  type GetRecipeQuery,
  type GetRecipeQueryVariables,
  type Recipe,
  type RecipeIngredient,
} from "src/graphql/generated/operations";
import { fetchConfigForRequest } from "src/app/utils/configLoader";
import type { NormalizedCacheObject } from "@apollo/client";
import { buildCanonicalUrl, buildSeoMeta, getPublicBaseUrl, toAbsoluteUrl } from "../utils/seo";
import { FormatIngredientDisplayName, FormatQuantityAndUnit, formatDisplayedTagName } from "src/graphql/utils";

const minutesToDuration = (minutes?: number | null) => {
  if (!minutes || minutes <= 0) {
    return undefined;
  }
  return `PT${Math.round(minutes)}M`;
};

const unique = (value: string, index: number, array: string[]) => array.indexOf(value) === index;

const formatStructuredIngredient = (item: RecipeIngredient): string => {
  const name = FormatIngredientDisplayName(
    item.ingredient.name ?? "",
    item.unit,
    item.ingredient.unitName ?? undefined,
    item.quantity
  );
  const quantity = FormatQuantityAndUnit(item.quantity, item.unit, 2, item.ingredient.unitName ?? undefined, name);
  const alternateQuantity = item.ingredientQuantity != null && String(item.ingredient.unit) !== String(item.unit)
    ? FormatQuantityAndUnit(item.ingredientQuantity, item.ingredient.unit, 2, item.ingredient.unitName ?? undefined, name, true)
    : null;
  return `${quantity}${alternateQuantity && alternateQuantity !== quantity ? ` (${alternateQuantity})` : ""} ${name}`.trim();
};

export const meta: Route.MetaFunction = ({ loaderData, params, location }) => {
  const recipe = loaderData?.recipe ?? null;

  if (!recipe) {
    return buildSeoMeta({
      location,
      title: `Recipe Not Found | ${brandName}`,
      description: `The recipe you requested is unavailable or has been removed. Discover similar dishes in ${brandName}.`,
      noindex: true,
    });
  }

  const macroBits: string[] = [];
  if (typeof recipe.calories === "number") {
    macroBits.push(`${Math.round(recipe.calories)} calories`);
  }
  if (typeof recipe.proteins === "number") {
    macroBits.push(`${Math.round(recipe.proteins)}g protein`);
  }
  if (typeof recipe.carbs === "number") {
    macroBits.push(`${Math.round(recipe.carbs)}g carbs`);
  } else if (typeof recipe.netCarbs === "number") {
    macroBits.push(`${Math.round(recipe.netCarbs)}g net carbs`);
  }
  if (typeof recipe.fat === "number") {
    macroBits.push(`${Math.round(recipe.fat)}g fat`);
  }

  const cuisineTags =
    recipe.tags
      ?.filter((tag) => tag.tag?.type === TagType.Cuisine)
      .map((tag) => tag.tag?.name?.trim())
      .filter((tagName): tagName is string => Boolean(tagName))
      .filter(unique) ?? [];

  const descriptionPieces = [
    recipe.recipeDescription?.trim(),
    macroBits.length ? `Per serving: ${macroBits.join(", ")}.` : null,
    `Add it to your ${brandName} plan or auto-build your smart grocery list.`,
  ].filter((piece): piece is string => Boolean(piece));
  const description = descriptionPieces.join(" ");

  const socialImage = recipe.imageUrl ?? undefined;
  const baseUrl = getPublicBaseUrl();
  const canonical = buildCanonicalUrl(location, baseUrl);
  const prepDuration = minutesToDuration(recipe.preparationTimeMin);
  const cookDuration = minutesToDuration(recipe.cookingTimeMin);
  const totalMinutes = (recipe.preparationTimeMin ?? 0) + (recipe.cookingTimeMin ?? 0);
  const totalDuration = totalMinutes > 0 ? minutesToDuration(totalMinutes) : undefined;

  const nutritionEntries: Record<string, string> = {};
  if (typeof recipe.calories === "number") {
    nutritionEntries.calories = `${Math.round(recipe.calories)} calories`;
  }
  if (typeof recipe.proteins === "number") {
    nutritionEntries.proteinContent = `${Math.round(recipe.proteins)} g`;
  }
  if (typeof recipe.carbs === "number") {
    nutritionEntries.carbohydrateContent = `${Math.round(recipe.carbs)} g`;
  } else if (typeof recipe.netCarbs === "number") {
    nutritionEntries.carbohydrateContent = `${Math.round(recipe.netCarbs)} g net carbs`;
  }
  if (typeof recipe.fat === "number") {
    nutritionEntries.fatContent = `${Math.round(recipe.fat)} g`;
  }
  if (typeof recipe.fibers === "number") {
    nutritionEntries.fiberContent = `${Math.round(recipe.fibers)} g`;
  }

  const structuredKeywords = recipe.tags
    ?.filter((tag) => tag.tag?.type === TagType.Auto)
    .map((tag) => formatDisplayedTagName(tag.tag?.name).trim())
    .filter((name): name is string => Boolean(name))
    .filter(unique) ?? [];
  const ingredients = recipe.consolidatedIngredients
    ?.filter((item) => item.ingredient.name?.trim())
    .map(formatStructuredIngredient) ?? [];
  const instructions = recipe.steps
    ?.filter((step) => step.description?.trim())
    .sort((a, b) => a.stepNumber - b.stepNumber)
    .map((step) => ({ "@type": "HowToStep", text: step.description.trim() })) ?? [];

  const structuredData: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Recipe",
    name: recipe.name,
    description: recipe.recipeDescription ?? description,
    image: toAbsoluteUrl(socialImage ?? "/logo-light.png", baseUrl),
    recipeCategory: recipe.category,
    recipeCuisine: cuisineTags.length ? cuisineTags : undefined,
    keywords: structuredKeywords.length ? structuredKeywords.join(", ") : undefined,
    recipeIngredient: ingredients.length ? ingredients : undefined,
    recipeInstructions: instructions.length ? instructions : undefined,
    recipeYield: recipe.serves > 0 ? recipe.serves : undefined,
    prepTime: prepDuration,
    cookTime: cookDuration,
    totalTime: totalDuration,
    nutrition:
      recipe.serves > 0 && Object.keys(nutritionEntries).length > 0
        ? {
            "@type": "NutritionInformation",
            ...nutritionEntries,
          }
        : undefined,
    aggregateRating: recipe.averageRating != null && recipe.averageRating > 0 && recipe.ratingCount > 0
      ? {
          "@type": "AggregateRating",
          ratingValue: recipe.averageRating,
          ratingCount: recipe.ratingCount,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined,
    datePublished: recipe.createdAt ?? undefined,
    mainEntityOfPage: canonical,
  };

  return buildSeoMeta({
    location,
    title: `${recipe.name} | ${brandName} Recipe`,
    description,
    type: "article",
    image: socialImage,
    imageAlt: `${recipe.name} plated`,
    publishedTime: recipe.createdAt ?? undefined,
    ldJson: structuredData,
  });
};

type LoaderData = {
  apolloState: NormalizedCacheObject | null;
  recipe: GetRecipeQuery["recipe"] | null;
};

export async function loader({ request, params, context }: Route.LoaderArgs) {
  const config = await fetchConfigForRequest(request, context);
  if (!config || !params.recid) {
    const payload: LoaderData = { apolloState: null, recipe: null };
    return data(payload, { status: 404 });
  }

  const client = gqlClientConnect(config.httpURL);

  let queryData: GetRecipeQuery | null | undefined;
  try {
    ({ data: queryData } = await client.query<GetRecipeQuery, GetRecipeQueryVariables>({
      query: GetRecipeDocument,
      variables: { id: params.recid },
    }));
  } catch (error) {
    // A GraphQL-level error (e.g. "recipe not found") means the recipe is
    // unavailable, not that the site is broken — fall through to the
    // not-found UI instead of the generic error boundary. Genuine network
    // failures still propagate so they surface as a real error.
    if (CombinedGraphQLErrors.is(error)) {
      queryData = null;
    } else {
      throw error;
    }
  }

  const apolloState = client.extract() as NormalizedCacheObject;

  const payload: LoaderData = {
    apolloState,
    recipe: queryData?.recipe ?? null,
  };

  // Keep the response status honest for crawlers/monitoring: a missing
  // recipe should still render the branded not-found UI, but as a 404.
  return payload.recipe ? payload : data(payload, { status: 404 });
}

export default function Recipe({ params }: Route.LoaderArgs) {
  return <Page recid={params.recid} />;
}
